﻿export default function dQuery(selectorOrElement: Function | string | HTMLElement | HTMLDocument | NodeListOf<Element> | Element[]) {
    if (selectorOrElement instanceof Function) {
        document.addEventListener("DOMContentLoaded", e => (selectorOrElement as Function)(e));
        return null;
    }

    if (selectorOrElement instanceof Array) {
        return new QueryResponse(selectorOrElement);
    }

    if (selectorOrElement instanceof HTMLElement || selectorOrElement instanceof HTMLDocument) {
        return new QueryResponse([selectorOrElement as HTMLElement]);
    }

    if (selectorOrElement instanceof NodeList) {
        const elements = [].slice.call(selectorOrElement as NodeListOf<Element>);
        return new QueryResponse(elements);
    }

    if (typeof selectorOrElement === "string") {
        return dQuery(document.querySelectorAll(selectorOrElement));
    }
}

class QueryResponse {
    private static handlers: { [name: string]: EventListener } = {};

    constructor(private elements: Element[]) {
        this.elements = !!elements ? elements.filter(e => e instanceof HTMLElement) : [];
        elements.forEach(e => {
            if (!!e && !e.id) {
                e.id = "_" + Math.random().toString(36).substr(2, 9);
            }
        });
    }

    private invoke(method: string, ...params: any[]): any[] {
        return this.elements.map(e => {
            return e[method].call(e, ...params);
        });
    }

    on(event: string, handler: EventListener) {
        handler = handler.bind(this);
        const name = event + ":" + this.elements.map(e => e.id).join(",");
        QueryResponse.handlers[name] = handler;
        this.invoke("addEventListener", event, handler);
        return this;
    }

    off(event: string) {
        const name = event + ":" + this.elements.map(e => e.id).join(",");
        const handler = QueryResponse.handlers[name];
        delete QueryResponse.handlers[name];
        this.invoke("removeEventListener", event, handler);
        return this;
    }

    once(event: string, handler: EventListener) {
        const specialHandler = (e) => {
            this.off(event);

            handler(e);
        };

        this.on(event, specialHandler);
        return this;
    }

    find(selector: string) {
        // convert NodeList to array
        const items = this.invoke('querySelectorAll', selector).map(l => Array.prototype.slice.call(l)) as Element[][];
        const flattened = items.reduce((a, b) => a.concat(b), []);
        return new QueryResponse(flattened);
    }

    parent() {
        return new QueryResponse(this.elements
            .map(e => e.parentElement as HTMLElement)
            .filter(value => !!value)
            .filter((value, index, self) => self.indexOf(value) === index));
    }

    children() {
        return new QueryResponse(this.elements
            .map(e => e.childNodes)
            .map(l => Array.prototype.slice.call(l))
            .reduce((a, b) => a.concat(b), [])
            .filter(n => n instanceof HTMLElement));
    }

    next(selector?: string) {
        const children = this.elements.map(e => {
            return dQuery(`#${e.id} ~ ${selector}`).elements;
        });
        const flattened = children.reduce((a, b) => a.concat(b), []);
        return new QueryResponse(flattened);
    }

    addClass(cls: string) {
        this.elements.forEach(e => e.classList.add(cls));
        return this;
    }

    removeClass(cls: string) {
        this.elements.forEach(e => e.classList.remove(cls));
        return this;
    }

    toggleClass(cls: string, force?: boolean) {
        this.elements.forEach(e => e.classList.toggle(cls, force));
        return this;
    }

    hasClass(cls: string) {
        return this.elements
            .map(e => e.classList.contains(cls))
            .filter(e => e)
            .length > 0;
    }

    text(text: string) {
        this.elements
            .forEach(e => e.textContent = text);
    }
}